import { useCallback } from 'react';
import { useToast } from '../components/Toast';
import { track, EVENTS } from '../services/analytics';
import { CANCELLED_CODES, IN_APP_CODE, signInTroubleCopy } from '../services/signInTrouble';
import { emailSignInAvailable } from '../services/emailLinkSignIn';
import { openSignInSheet } from '../services/signInSheet';
import { rememberDestination } from '../utils/postSignIn';

// Pages that finish their own work from a credential in the address (the
// claim and feedback links): an email link would land them without it, so
// they keep the copy-link notice.
const TOKEN_PAGES = ['/claim-credits', '/feedback'];

// The one way a failed Google sign-in reaches the student (#2457): what
// happened, what to do, and a button that does it. Before this, a network
// error on /pricing, the claim page or the feedback page showed nothing at
// all, and the landing toast had no way back but finding the button again.
// A cancelled popup is the student's own choice and stays quiet.
//
// notify(error, retry): `retry` re-runs the caller's own sign-in, so the
// retry lands wherever that caller was going (Stripe, the claim, the app).
// useToast throws outside its provider; every app route sits inside it, but
// a unit harness may not, and a sign-in button must never crash its page.
function useToastIfMounted() {
    try { return useToast(); } catch { return null; }
}

// The page's address without credential-bearing params (security review,
// #2457): the claim/feedback pages scrub theirs on load, but a copied link
// must stay safe on any page a sign-in button is ever added to.
const CREDENTIAL_PARAMS = ['token', 'grant', 'g', 'e', 'invite', 'session_id'];
export function shareableHref() {
    const url = new URL(window.location.href);
    for (const p of CREDENTIAL_PARAMS) url.searchParams.delete(p);
    return url.toString();
}

export default function useSignInNotice() {
    const toast = useToastIfMounted();
    return useCallback((error, retry) => {
        if (CANCELLED_CODES.includes(error?.code)) return false;
        // Inside an app's browser with the email link on (#3928): the sheet,
        // email first, instead of a notice whose only easy action was
        // Google's refusal. The finished sign-in lands back on this page.
        if (error?.code === IN_APP_CODE && emailSignInAvailable()
            && !TOKEN_PAGES.includes(window.location.pathname)) {
            rememberDestination(window.location.pathname);
            openSignInSheet({ mode: 'in_app', entry: error.entry, app: error.app || null });
            return true;
        }
        const copy = signInTroubleCopy(error);
        const onClick = () => {
            try { track(EVENTS.SIGNIN_RECOVERY_CLICKED, { reason: copy.reason, action: copy.action }); } catch { /* never block */ }
            if (copy.action === 'copy_link') {
                try { navigator.clipboard?.writeText(shareableHref()); } catch { /* clipboard refused */ }
                return;
            }
            try { retry?.(); } catch { /* the retry reports its own failure */ }
        };
        try {
            toast?.error(copy.title, copy.message, {
                // Long enough to read on a phone and reach the button; the
                // in-app instructions are longer still.
                duration: copy.action === 'copy_link' ? 30000 : 15000,
                action: { label: copy.actionLabel, onClick },
            });
        } catch { /* toast unavailable — never break sign-in */ }
        return true;
    }, [toast]);
}
